import { getForexRates } from "@/lib/api/hive.ts"

export type CurrencyCode =
	| "USD"
	| "EUR"
	| "GBP"
	| "JPY"
	| "AUD"
	| "CAD"
	| "CHF"
	| "HKD"
	| "SGD"
	| "SEK"
	| "NOK"
	| "DKK"
	| "NZD"
	| "ZAR"
	| "KRW"
	| "INR"
	| "BRL"
	| "MXN"
	| "CNY"
	| "RON"
	| "TWD"

export interface CurrencyConfig {
	code: CurrencyCode
	symbol: string
	name: string
	flag: string
	rate: number // Exchange rate relative to 1 USD
	locale: string // For Intl.NumberFormat
}

// Hardcoded rates (Snapshot)
export const CURRENCIES: Record<CurrencyCode, CurrencyConfig> = {
	USD: { code: "USD", symbol: "$",    name: "US Dollar",           flag: "🇺🇸", rate: 1.0,    locale: "en-US" },
	EUR: { code: "EUR", symbol: "€",    name: "Euro",                flag: "🇪🇺", rate: 0.85,   locale: "de-DE" },
	GBP: { code: "GBP", symbol: "£",    name: "British Pound",       flag: "🇬🇧", rate: 0.74,   locale: "en-GB" },
	JPY: { code: "JPY", symbol: "¥",    name: "Japanese Yen",        flag: "🇯🇵", rate: 149.5,  locale: "ja-JP" },
	AUD: { code: "AUD", symbol: "A$",   name: "Australian Dollar",   flag: "🇦🇺", rate: 1.49,   locale: "en-AU" },
	CAD: { code: "CAD", symbol: "C$",   name: "Canadian Dollar",     flag: "🇨🇦", rate: 1.37,   locale: "en-CA" },
	CHF: { code: "CHF", symbol: "Fr",   name: "Swiss Franc",         flag: "🇨🇭", rate: 0.88,   locale: "de-CH" },
	HKD: { code: "HKD", symbol: "HK$",  name: "Hong Kong Dollar",    flag: "🇭🇰", rate: 7.8,    locale: "en-HK" },
	SGD: { code: "SGD", symbol: "S$",   name: "Singapore Dollar",    flag: "🇸🇬", rate: 1.35,   locale: "en-SG" },
	SEK: { code: "SEK", symbol: "kr",   name: "Swedish Krona",       flag: "🇸🇪", rate: 10.4,   locale: "sv-SE" },
	NOK: { code: "NOK", symbol: "kr",   name: "Norwegian Krone",     flag: "🇳🇴", rate: 10.5,   locale: "nb-NO" },
	DKK: { code: "DKK", symbol: "kr",   name: "Danish Krone",        flag: "🇩🇰", rate: 6.33,   locale: "da-DK" },
	NZD: { code: "NZD", symbol: "NZ$",  name: "New Zealand Dollar",  flag: "🇳🇿", rate: 1.63,   locale: "en-NZ" },
	ZAR: { code: "ZAR", symbol: "R",    name: "South African Rand",  flag: "🇿🇦", rate: 18.2,   locale: "en-ZA" },
	KRW: { code: "KRW", symbol: "₩",    name: "South Korean Won",    flag: "🇰🇷", rate: 1370,   locale: "ko-KR" },
	INR: { code: "INR", symbol: "₹",    name: "Indian Rupee",        flag: "🇮🇳", rate: 83.5,   locale: "en-IN" },
	BRL: { code: "BRL", symbol: "R$",   name: "Brazilian Real",      flag: "🇧🇷", rate: 5.0,    locale: "pt-BR" },
	MXN: { code: "MXN", symbol: "MX$",  name: "Mexican Peso",        flag: "🇲🇽", rate: 17.2,   locale: "es-MX" },
	CNY: { code: "CNY", symbol: "¥",    name: "Chinese Yuan",        flag: "🇨🇳", rate: 7.25,   locale: "zh-CN" },
	RON: { code: "RON", symbol: "lei",  name: "Romanian Leu",        flag: "🇷🇴", rate: 4.34,   locale: "ro-RO" },
	TWD: { code: "TWD", symbol: "NT$",  name: "New Taiwan Dollar",   flag: "🇹🇼", rate: 31.8,   locale: "zh-TW" },
}

const CURRENCY_ALIASES: Record<string, string> = {
	GBX: "GBP",
	GBPX: "GBP",
	ILA: "ILS",
	ZAC: "ZAR",
}

const FALLBACK_RATES: Record<string, number> = {
	USD: 1.0,
	EUR: 0.85,
	GBP: 0.74,
	JPY: 149.5,
	AUD: 1.49,
	CAD: 1.37,
	CHF: 0.88,
	HKD: 7.8,
	SGD: 1.35,
	SEK: 10.4,
	NOK: 10.5,
	DKK: 6.33,
	NZD: 1.63,
	ZAR: 18.2,
	KRW: 1370,
	INR: 83.5,
	BRL: 5.0,
	MXN: 17.2,
	CNY: 7.25,
	RON: 4.34,
	TWD: 31.8,
	ILS: 3.63,
}

// All exchange rates relative to USD — pre-populated from hardcoded rates,
// then updated with live rates by updateRates().
// Used for cross-rate conversion (source currency → display currency).
export const allRates: Record<string, number> = { ...FALLBACK_RATES }

export function isCurrencyCode(code: string | undefined): code is CurrencyCode {
	return !!code && code in CURRENCIES
}

export function normalizeCurrencyCode(code?: string | null): string | undefined {
	if (!code) return undefined
	const normalized = code.trim().toUpperCase()
	if (!normalized) return undefined
	return CURRENCY_ALIASES[normalized] ?? normalized
}

export function getRate(code?: string | null): number | undefined {
	const normalized = normalizeCurrencyCode(code)
	return normalized ? allRates[normalized] : undefined
}

function roundAmount(amount: number): number {
	return Math.round(amount * 100) / 100
}

export function convertCurrencyAmount(amount: number, toCurrency: string, fromCurrency?: string): number {
	const toRate = getRate(toCurrency)
	if (!fromCurrency || fromCurrency.trim() === "") {
		// No source currency: assume the amount is already USD-denominated.
		// If the target rate is unknown we cannot convert — fail loudly.
		return toRate == null ? NaN : roundAmount(amount * toRate)
	}

	const fromRate = getRate(fromCurrency)
	// A genuinely missing rate must NOT silently pass through 1:1 — that would
	// inflate totals (e.g. a TWD amount summed into a USD total). Return NaN so
	// the missing-rate condition is loud and can be guarded at render time.
	if (fromRate == null || toRate == null || fromRate === 0) {
		return NaN
	}

	return roundAmount(amount * (toRate / fromRate))
}

/**
 * Fetches latest rates with USD as the base and updates the CURRENCIES object.
 *
 * Hive is the single authoritative source of FX rates (USD-based). Unlike the
 * previous ECB/frankfurter source, it includes EODHD-supplemented currencies
 * such as TWD, so every supported display currency converts correctly. The
 * static FALLBACK_RATES remain as an offline backstop if the request fails.
 */
export async function updateRates(): Promise<boolean> {
	try {
		const { rates } = await getForexRates()

		if (rates) {
			for (const code of Object.keys(CURRENCIES) as CurrencyCode[]) {
				if (code === "USD") {
					CURRENCIES[code].rate = 1.0
					continue
				}
				const rate = rates[code]
				if (typeof rate === "number") {
					CURRENCIES[code].rate = rate
				}
			}

			// Store all rates for cross-rate source currency conversion
			Object.entries(rates).forEach(([code, rate]) => {
				const normalized = normalizeCurrencyCode(code)
				if (normalized) {
					allRates[normalized] = rate
				}
			})
			// USD base rate is always 1.0
			allRates["USD"] = 1.0
			return true
		}
	} catch (error) {
		console.error("Failed to update currency rates:", error)
		// Logic to handle failure (e.g., keep using hardcoded fallback rates)
	}
	return false
}

// Helper to guess currency from Timezone
export function getDefaultCurrency(): CurrencyCode {
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

	if (timeZone.startsWith("Asia/Tokyo"))                                        return "JPY"
	if (timeZone.startsWith("Europe/Zurich") || timeZone === "Europe/Vaduz")      return "CHF"
	if (timeZone.startsWith("Asia/Singapore"))                                    return "SGD"
	if (timeZone.startsWith("Asia/Hong_Kong"))                                    return "HKD"
	if (timeZone.startsWith("Europe/Stockholm"))                                  return "SEK"
	if (timeZone.startsWith("Europe/Oslo"))                                       return "NOK"
	if (timeZone.startsWith("Europe/Copenhagen"))                                 return "DKK"
	if (timeZone.startsWith("Pacific/Auckland"))                                  return "NZD"
	if (timeZone.startsWith("Africa/Johannesburg"))                               return "ZAR"
	if (timeZone.startsWith("Asia/Seoul"))                                        return "KRW"
	if (timeZone.startsWith("Asia/Kolkata") || timeZone.startsWith("Asia/Calcutta")) return "INR"
	if (timeZone.startsWith("America/Sao_Paulo") || timeZone.startsWith("America/Recife")) return "BRL"
	if (timeZone.startsWith("America/Mexico_City"))                               return "MXN"
	if (timeZone.startsWith("Asia/Shanghai") || timeZone.startsWith("Asia/Chongqing")) return "CNY"
	if (timeZone.startsWith("Europe/Bucharest"))                                  return "RON"
	if (timeZone.startsWith("Europe/London"))                                     return "GBP"
	if (timeZone.startsWith("Europe/"))                                           return "EUR" // Broad fallback
	if (timeZone.startsWith("Australia/"))                                        return "AUD"
	if (timeZone.startsWith("America/Toronto") || timeZone.startsWith("America/Vancouver")) return "CAD"

	return "USD" // Default fallback
}
